/* eslint-disable import/no-extraneous-dependencies */
import axios from 'axios';
// eslint-disable-next-line import/no-unresolved
import mime from 'browserify-mime';
import { fr } from 'date-fns/locale';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useField, useFormikContext } from 'formik';

import {
  Autocomplete,
  FormControlLabel,
  FormGroup,
  TextField,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';

import { mapObjectValsTo } from './mapper';

dayjs.extend(utc);
dayjs.extend(timezone);

export { v1 as uuid } from 'uuid';
export * from './certificate';
export * from './hooks';
export * from './mapper';
export * from './tus';

export const FORGOT_PASSWORD_EMAIL = 'fpe';
export const FORGOT_PASSWORD_RESULT = 'fpr';

export const nop = () => {};
export const nopa = async () => {};

export const verifyClinicId = (id, clinicIds) => {
  if (clinicIds.includes(String(id))) {
    return true;
  }
  return false;
};

export const capitalizeWords = (sentence) =>
  sentence
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

export const dropItemFrom = (obj) => (key) => {
  const { [key]: _, ...rest } = { ...obj };
  return rest;
};

export const getTextWidth = (text, font) => {
  const offScreenElement = document.createElement('span');
  offScreenElement.style.position = 'absolute';
  offScreenElement.style.visibility = 'hidden';
  offScreenElement.style.width = 'auto';
  offScreenElement.style.font = font;
  offScreenElement.textContent = text;

  document.body.appendChild(offScreenElement);
  const width = offScreenElement.offsetWidth;
  document.body.removeChild(offScreenElement);

  return width;
};

export const openLinkInNewTab = (href, options = {}) => {
  const link = document.createElement('a');
  Object.assign(link, {
    ...options,
    style: { display: 'none' },
    target: '_blank',
    rel: 'noopener noreferrer',
    href: href.replace(/%20/g, '+'),
  });
  link.click();
  link.remove();
};

export const openLinkInPopUpWindow = (
  url,
  windowTitle = 'Untitled',
  width = 960,
  height = 540,
  win = window,
) => {
  const left = Number(win.screen.width / 2.12 - width / 2);
  const top = Number(win.screen.height / 3.13 - height / 2);
  const popUpWindow = win.open(
    url,
    '_blank',
    `scrollbars=no,location=no,directories=no,menubar=no,toolbar=no,status=no,width=${width},height=${height},top=${top},left=${left}`,
  );

  popUpWindow.onload = () => {
    popUpWindow.document.title = windowTitle;
  };
  popUpWindow.document.title = windowTitle;

  return false;
};

export const openLinkWithEmbeddedPDF = (pdfUrl) => {
  const iframe = document.createElement('iframe');
  const container = document.createElement('div');
  container.style.position = 'relative';
  container.style.top = 0;
  container.style.bottom = 0;

  iframe.src = pdfUrl;
  iframe.style.width = '100%';
  iframe.style.height = '500px'; // Adjust the height as needed
  iframe.style.zIndex = 9999;
  iframe.style.position = 'absolute';

  container.appendChild(iframe);
  document.body.appendChild(container);
};

export const fallbackCopyTextToClipboard = (text) => {
  let textArea = null;

  if (text) {
    textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.display = 'none';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
  }

  try {
    const isSuccess = document.execCommand('copy');
    const msg = isSuccess ? 'successful' : 'unsuccessful';
    console.log(`Fallback: Copying text command was ${msg}`);
  } catch (err) {
    throw new Error(`Fallback: Oops, unable to copy ${err}`);
  }

  if (text && textArea) document.body.removeChild(textArea);
};

export const copyTextToClipboard = async (text) => {
  if (!navigator.clipboard) {
    // eslint-disable-next-line no-unneeded-ternary
    fallbackCopyTextToClipboard(text ? text : undefined);
    return;
  }
  try {
    // eslint-disable-next-line no-unneeded-ternary
    navigator.clipboard.writeText(text ? text : '');
  } catch (e) {
    throw new Error(`Async: Could not copy text: ${e}`);
  }
};

export const setTimeoutAsync = async (cb, milliseconds) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(cb());
    }, milliseconds);
  });
};

export const noop = () => {};

export function* range(start, end, step) {
  while (start < end) {
    yield start;
    // eslint-disable-next-line no-param-reassign
    start += step;
  }
}

export const fileNameFromUrl = (url) => {
  // eslint-disable-next-line no-useless-escape
  const matches = url.match(/\/([^\/?#]+)[^\/]*$/);
  if (matches.length > 1) {
    return matches[1];
  }
  return null;
};

export const createFileFromImagePath = (imagePath) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, img.width, img.height);

      canvas.toBlob((blob) => {
        if (blob) {
          const fileName = fileNameFromUrl(imagePath) || 'file';
          const file = new File([blob], fileName, { type: blob.type });
          resolve(file);
        } else {
          reject(new Error('Failed to create File object.'));
        }
      }, img.type);
    };

    img.onerror = (error) => {
      reject(error);
    };

    img.src = imagePath;
  });
};

export const downloadAsFile = async (url) => {
  try {
    const response = await axios.get(url, { responseType: 'blob' });

    // Create a File object from the blob response
    const file = new File(
      [response.data],
      fileNameFromUrl(url) || 'downloadedFile',
      {
        type: response.headers['content-type'],
      },
    );

    return file;
  } catch (error) {
    console.error('Error downloading file:', error);
    return null;
  }
};

export const downloadFile = (file, fileName, replace = false) => {
  const fileExtension = mime.extension(file.type);
  const defaultFileName = fileName || file.name || 'download';

  let fullFileName;
  if (replace) {
    fullFileName = fileName;
  } else {
    if (defaultFileName.endsWith(`.${fileExtension}`)) {
      fullFileName = defaultFileName;
    } else {
      fullFileName = `${defaultFileName}.${fileExtension}`;
    }
  }

  const href = URL.createObjectURL(file);
  const link = document.createElement('a');

  link.href = href;
  link.setAttribute('download', fullFileName);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(href);
};

export const renameFileFromContentDisposition = (
  originalFile,
  responseHeaders,
) => {
  if (!originalFile || !responseHeaders) {
    return null;
  }

  const fileBits = [originalFile];

  const contentDisposition =
    typeof responseHeaders.get === 'function'
      ? responseHeaders.get('content-disposition')
      : responseHeaders['content-disposition'];

  if (contentDisposition) {
    const param = 'filename=';

    let startIndex = contentDisposition.indexOf(param);

    if (startIndex !== -1) {
      let filename = contentDisposition
        .substring(startIndex + param.length)
        .trim();

      if (filename.startsWith('"') && filename.endsWith('"')) {
        filename = filename.slice(1, -1);
      }

      return new File(fileBits, filename, {
        type: originalFile.type,
        lastModified: originalFile.lastModified,
      });
    }
  }

  if (!originalFile.name) {
    return new File(fileBits, 'download', {
      type: originalFile.type,
      lastModified: originalFile.lastModified,
    });
  }

  return originalFile;
};

export const getSparsedRandomValue = (
  min = 0,
  max = 1000,
  previousValue = 0,
  minGap = 10,
  maxGap = 900,
) => {
  const gap = Math.random() * (maxGap - minGap) + minGap;
  const offset = Math.random() * gap - gap / 2;
  const nextValue = previousValue + offset;
  const clampedNextValue = Math.min(Math.max(nextValue, min), max);

  return clampedNextValue;
};

export const blobToFile = (
  blob,
  name = 'file',
  type,
  lastModified = new Date().getTime(),
  encoding = 'utf-8',
) => new File([blob], name, { type, lastModified }, encoding);

export const fileToStringDataAsync = async (file) => {
  const blob = await fileToByteArrayAsync(file);
  const encodedBlob = base64.fromByteArray(new Uint8Array(blob));
  return {
    name: file.name,
    type: file.type,
    lastModified: file.lastModified,
    base64data: encodedBlob,
  };
};

export const swapObject = (obj) =>
  Object.entries({ ...obj }).reduce((acc, [key, value]) => {
    acc[value] = key;
    return acc;
  }, {});

export const formatDateWithTimezone = (
  date,
  time,
  timeZone = 'Asia/Singapore',
) => {
  const dateTime = dayjs(date)
    .hour(dayjs(time).hour())
    .minute(dayjs(time).minute());
  return dateTime.tz(timeZone);
};

const formatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

export const formatDate = (date) => {
  return new Date(date)
    .toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Singapore',
    })
    .replace('am', 'AM')
    .replace('pm', 'PM');
};

export const formatShortDate = (date) => {
  return new Date(date)
    .toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour12: true,
      timeZone: 'Singapore',
    })
    .replace('am', 'AM')
    .replace('pm', 'PM');
};

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function formatRole(role) {
  if (!role) return 'No role';

  return role
    .split(/[._]/)
    .map((word) => capitalize(word))
    .join(' ');
}

export const parseJsonFromBlob = async (blob) => {
  try {
    const jsonString = await blob.text();
    const jsonData = JSON.parse(jsonString);
    return jsonData;
  } catch (error) {
    console.error('Error parsing JSON from Blob:', error);
    return null;
  }
};

export const getBoolOrNone = (val) => {
  const value =
    typeof val === 'string' ? val.replace(/\s/g, '').toLowerCase() : '';

  if (
    value === 'true' ||
    value === '1' ||
    value === 'y' ||
    value === 'yes' ||
    value === 1 ||
    String(value) === 'true'
  ) {
    return true;
  }

  if (
    value === 'false' ||
    value === '0' ||
    value === 'n' ||
    value === 'no' ||
    value === 0 ||
    String(value) === 'false'
  ) {
    return false;
  }

  return undefined;
};

export const getIntOrNone = (val) => {
  const value = typeof val === 'string' ? val.replace(/\s/g, '') : null;
  const parsed = parseInt(value, 10);

  if (!Number.isNaN(parsed)) return parsed;
  return undefined;
};

const options = [
  { value: 'doctor', label: 'Doctors' },
  { value: 'clinic_assistants', label: 'Clinic Assistants' },
  { value: 'nurse', label: 'Nurses' },
  { value: 'phlebotomist', label: 'Phlebotomist' },
];

export const CheckboxGroup = ({ selectedValues = [], onChange }) => {
  return (
    <div className='text-md py-2 px-2 text-gray-600'>
      <FormGroup>
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            control={
              <Checkbox
                className='py-1'
                style={{ color: '#1788b9' }}
                checked={
                  Array.isArray(selectedValues) &&
                  selectedValues.includes(option.value)
                }
                onChange={(e) => onChange(option.value, e.target.checked)}
              />
            }
            label={option.label}
          />
        ))}
      </FormGroup>
    </div>
  );
};

export function FormikAutocomplete({
  name,
  options,
  getOptionLabel,
  label,
  ...props
}) {
  const { setFieldValue } = useFormikContext();
  const [field, meta] = useField(name);

  return (
    <Autocomplete
      {...props}
      options={options}
      getOptionLabel={getOptionLabel}
      value={options.find((opt) => opt._id === field.value) || null}
      onChange={(_, newValue) => {
        setFieldValue(name, newValue?._id || '');
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder='Select one'
          label={label}
          error={meta.touched && Boolean(meta.error)}
          helperText={meta.touched && meta.error}
        />
      )}
    />
  );
}

export function convertToJPG(file, scale = 2) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const img = new Image();

    reader.onload = function (e) {
      img.src = e.target?.result;
    };

    img.onload = function () {
      const width = img.width * scale;
      const height = img.height * scale;

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');

      if (ctx) {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
      }

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const cleanName = file.name
              .replace(/(resized-\d+-)+/g, '')
              .replace(/\.(png|jpg|jpeg)$/i, '');

            const jpgFile = new File([blob], `${cleanName}.jpg`, {
              type: 'image/jpeg',
            });

            resolve(jpgFile);
          } else {
            reject(new Error('Conversion to JPG failed'));
          }
        },
        'image/jpeg',
        0.95,
      );
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const scrollToTop = () => {
  try {
    window?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  } catch {
    // skip
  }
};

/**
 * Recursively removes undefined values from objects and arrays
 * @param {*} value - The value to clean (object, array, or primitive)
 * @returns {*} - The cleaned value with undefined removed
 */
export function removeUndefined(value) {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .filter((item) => item !== undefined)
      .map((item) => removeUndefined(item));
  }

  const cleaned = {};
  for (const key in value) {
    if (value.hasOwnProperty(key)) {
      const val = value[key];
      if (val !== undefined) {
        cleaned[key] = removeUndefined(val);
      }
    }
  }
  return cleaned;
}

/**
 * Recursively removes nullish values (null and undefined) from objects and arrays
 * @param {*} value - The value to clean (object, array, or primitive)
 * @returns {*} - The cleaned value with nullish values removed
 */
export function removeNullish(value) {
  if (value === null || value === undefined || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .filter((item) => item != null)
      .map((item) => removeNullish(item));
  }

  const cleaned = {};
  for (const key in value) {
    if (value.hasOwnProperty(key)) {
      const val = value[key];
      if (val != null) {
        cleaned[key] = removeNullish(val);
      }
    }
  }
  return cleaned;
}
